import { useState } from "react";
import { MessageCircle, Send, MapPin, Clock, Phone } from "lucide-react";
import { HOME } from "@/constants/testIds";

const PHONE_DISPLAY = "+91 83104 76246";
const PHONE = "+918310476246";
const WA_URL = "https://wa.me/918310476246";

const BookingForm = () => {
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        phone: "",
        destination: "",
        dates: "",
        message: "",
    });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const buildWaHref = () => {
        const text = [
            `Hi Budget Trips! I'd like to plan a trip.`,
            form.name && `Name: ${form.name}`,
            form.phone && `Phone: ${form.phone}`,
            form.destination && `Destination: ${form.destination}`,
            form.dates && `Dates: ${form.dates}`,
            form.message && `Notes: ${form.message}`,
        ]
            .filter(Boolean)
            .join("\n");
        return `${WA_URL}?text=${encodeURIComponent(text)}`;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        // Open WhatsApp with a prefilled message so the enquiry actually reaches us.
        window.open(buildWaHref(), "_blank", "noopener");
    };

    const inputCls =
        "w-full rounded-lg border border-[#E5E7EB] bg-white px-4 py-3 text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1B3B2B]/30 focus:border-[#1B3B2B] transition-colors";

    return (
        <section id="plan" className="mx-auto max-w-7xl px-5 md:px-10 py-24">
            <div className="grid gap-12 lg:grid-cols-2">
                <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-[#C46A42] font-medium">
                        Let's plan it
                    </p>
                    <h2 className="mt-3 font-display text-4xl md:text-6xl text-[#1B3B2B] leading-[1.05]">
                        Your next journey is one message away.
                    </h2>
                    <p className="mt-5 text-[#4B5563] max-w-lg leading-relaxed">
                        Tell us where you'd like to go. Known for our quick
                        response, we'll get back with a friendly, budget-friendly
                        plan.
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-full bg-[#1B3B2B]/10 text-[#1B3B2B]">
                                <MapPin className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="font-display text-lg text-[#1B3B2B]">
                                    Our Office
                                </div>
                                <div className="text-[#4B5563]">
                                    Near KEB Office, MRS Circle, Vidya Nagar,
                                    Shivamogga, Karnataka
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-full bg-[#1B3B2B]/10 text-[#1B3B2B]">
                                <Clock className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="font-display text-lg text-[#1B3B2B]">
                                    Open 24 Hours
                                </div>
                                <div className="text-[#4B5563]">
                                    Round-the-clock support, all 7 days
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-full bg-[#1B3B2B]/10 text-[#1B3B2B]">
                                <Phone className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="font-display text-lg text-[#1B3B2B]">
                                    Call us
                                </div>
                                <div className="text-[#4B5563]">
                                    {PHONE_DISPLAY} — quick response guaranteed
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-10"
                >
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label className="text-sm text-[#4B5563] mb-1.5 block">
                                Your name
                            </label>
                            <input
                                data-testid={HOME.formName}
                                required
                                value={form.name}
                                onChange={update("name")}
                                placeholder="e.g. Ananya Kulkarni"
                                className={inputCls}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-sm text-[#4B5563] mb-1.5 block">
                                Phone number
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2.5">
                                <input
                                    data-testid={HOME.formPhone}
                                    required
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    placeholder="+91 …"
                                    className={inputCls}
                                />
                                <a
                                    data-testid={HOME.formWhatsapp}
                                    href={buildWaHref()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-white whitespace-nowrap hover:bg-[#1EAE53] hover:-translate-y-0.5 transition-[transform,background-color] duration-200 shadow"
                                >
                                    <MessageCircle className="h-4 w-4" /> Chat on
                                    WhatsApp
                                </a>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-[#4B5563] mb-1.5 block">
                                Destination
                            </label>
                            <input
                                data-testid={HOME.formDestination}
                                value={form.destination}
                                onChange={update("destination")}
                                placeholder="Chikmagalur, Jog Falls…"
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className="text-sm text-[#4B5563] mb-1.5 block">
                                Travel dates
                            </label>
                            <input
                                data-testid={HOME.formDates}
                                value={form.dates}
                                onChange={update("dates")}
                                placeholder="12 Feb – 14 Feb"
                                className={inputCls}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-sm text-[#4B5563] mb-1.5 block">
                                Anything we should know?
                            </label>
                            <textarea
                                data-testid={HOME.formMessage}
                                rows={4}
                                value={form.message}
                                onChange={update("message")}
                                placeholder="Group size, budget, preferences…"
                                className={inputCls + " resize-none"}
                            />
                        </div>
                    </div>

                    <button
                        data-testid={HOME.formSubmit}
                        type="submit"
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1B3B2B] px-6 py-3.5 text-white hover:bg-[#2C5B42] hover:-translate-y-0.5 transition-[transform,background-color] duration-200 shadow-md"
                    >
                        Start Planning <Send className="h-4 w-4" />
                    </button>

                    {submitted && (
                        <p className="mt-4 text-sm text-[#1B3B2B]">
                            Opening WhatsApp with your details… if it didn't
                            open, tap "Chat on WhatsApp" above.
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
};

export default BookingForm;
